import { FC, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import "./dev.scss";

import { listOrg, postOrgBootstrap, RubiOrgModel } from "@rubi/api-generated";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";

const DEFAULT_TIMEZONE = "America/Toronto";

export const DevOrgConfigListPage: FC = () => {
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<RubiOrgModel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  // Bootstrap form state
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [city, setCity] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [apiRegion, setApiRegion] = useState("ca");
  const [stytchSlug, setStytchSlug] = useState("");
  const [rubiOrgId, setRubiOrgId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [bootstrapNote, setBootstrapNote] = useState<string | null>(null);

  const loadOrgs = async () => {
    const response = await listOrg({ throwOnError: false });
    if (response.status === 200) {
      setOrgs(response.data ?? []);
      setError(null);
    } else {
      setError(`${response.status} ${response.error?.console_msg ?? ""}`);
    }
  };

  useEffect(() => {
    loadOrgs();
  }, []);

  const filtered = orgs.filter((o) => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (
      o.id.toLowerCase().includes(f) ||
      (o.title ?? "").toLowerCase().includes(f) ||
      (o.short_title ?? "").toLowerCase().includes(f)
    );
  });

  const trimmedRubiOrgId = rubiOrgId.trim();
  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const rubiOrgIdInvalid =
    trimmedRubiOrgId.length > 0 && !UUID_RE.test(trimmedRubiOrgId);

  const canSubmit =
    title.trim().length > 0 &&
    city.trim().length > 0 &&
    contactEmail.trim().includes("@") &&
    !rubiOrgIdInvalid &&
    !submitting;

  const handleBootstrap = async () => {
    setSubmitting(true);
    setBootstrapError(null);
    try {
      const response = await postOrgBootstrap({
        body: {
          title: title.trim(),
          short_title: shortTitle.trim() || null,
          city: city.trim(),
          contact_email: contactEmail.trim(),
          timezone: timezone.trim() || null,
          api_region: apiRegion,
          stytch_slug: stytchSlug.trim() || null,
          rubi_org_id: trimmedRubiOrgId || null,
        },
      });
      const result = response.data!;
      setBootstrapNote(
        `Created org ${result.org.id} (Stytch ${result.stytch_org_id}). Redirecting…`,
      );
      setTitle("");
      setShortTitle("");
      setCity("");
      setContactEmail("");
      setStytchSlug("");
      setRubiOrgId("");
      await loadOrgs();
      navigate(`/dev_admin/org_config/${result.org.id}`);
    } catch (e) {
      setBootstrapError(
        e instanceof Error ? e.message : "Failed to bootstrap org",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DevPage
      title="Org Config"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>Org Config</Crumb>
        </Breadcrumbs>
      }
    >
      <Section subtitle="Bootstrap a new org">
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a Stytch B2B organization, inserts a <code>rubi_org</code>{" "}
            row carrying its ID, and seeds <code>rv2_org_config</code> with
            opinionated defaults. URLs and other per-org details are
            placeholders — refine them via the Org Config editor after the
            redirect.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                helperText="Used as the display name in Stytch and rubi_org.title"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Short title"
                value={shortTitle}
                onChange={(e) => setShortTitle(e.target.value)}
                helperText="Optional; also used as default tracking number prefix"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Contact email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                helperText="Receives notification emails by default"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                helperText="IANA timezone"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="API region"
                value={apiRegion}
                onChange={(e) => setApiRegion(e.target.value)}
              >
                <MenuItem value="ca">ca</MenuItem>
                <MenuItem value="us">us</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Stytch slug (optional)"
                value={stytchSlug}
                onChange={(e) => setStytchSlug(e.target.value)}
                helperText="Derived from title if blank"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Rubi Org ID (optional)"
                value={rubiOrgId}
                onChange={(e) => setRubiOrgId(e.target.value)}
                error={rubiOrgIdInvalid}
                helperText={
                  rubiOrgIdInvalid
                    ? "Must be a valid UUID (e.g. 11111111-2222-3333-4444-555555555555)"
                    : "Paste an existing rubi_org_id to preserve it across environments (test → prod). Leave blank to generate a new one."
                }
              />
            </Grid>
          </Grid>

          {bootstrapError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {bootstrapError}
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleBootstrap}
              disabled={!canSubmit}
            >
              {submitting ? "Bootstrapping…" : "Bootstrap org"}
            </Button>
          </Stack>
        </Paper>
      </Section>

      <Section subtitle="Existing orgs">
        <ErrorMsg msg={error} />
        <Box sx={{ mb: 1 }}>
          <input
            type="search"
            placeholder="filter by id or title"
            aria-label="Filter existing orgs by id or title"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            name="org-config-list-filter"
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            data-bwignore="true"
            style={{ width: 320 }}
          />
        </Box>
        <table className="dev-table">
          <thead>
            <tr>
              <th>id</th>
              <th>title</th>
              <th>short_title</th>
              <th>stytch_org_id</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((org) => (
              <tr key={org.id}>
                <td className="mono">{org.id}</td>
                <td>{org.title}</td>
                <td>{org.short_title ?? <span className="muted">—</span>}</td>
                <td className="mono">
                  {org.stytch_org_id ?? <span className="muted">—</span>}
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <Link to={`/dev_admin/org/${org.id}`}>edit org</Link>
                  <span className="muted" style={{ margin: "0 6px" }}>
                    ·
                  </span>
                  <Link to={`./${org.id}`}>edit config</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Snackbar
        open={bootstrapNote != null}
        autoHideDuration={4000}
        onClose={() => setBootstrapNote(null)}
      >
        <Alert severity="success" onClose={() => setBootstrapNote(null)}>
          {bootstrapNote ?? ""}
        </Alert>
      </Snackbar>
    </DevPage>
  );
};
